import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, Typography, Box, LinearProgress, Chip, Grid, Alert, Button, IconButton, Menu, MenuItem, CircularProgress, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText, Tabs, Tab, TextField } from '@mui/material';
import { CheckCircle, Warning, Error as ErrorIcon, HourglassEmpty, TrendingUp, MoreVert, Check, Close, Schedule, RocketLaunch, InfoOutlined, HelpOutline, ChevronLeft, ChevronRight, Replay, PlayArrow } from '@mui/icons-material';
import { useSiteStore } from '../../store/site.store';
import { useSiteMode, useSiteReadiness } from '../../hooks/useMode';
import { useActions, useActionStats } from '../../hooks/useActions';
import { useTransitionEligibility } from '../../hooks/useTransition';
import { ReadinessPillar } from '../../types/mode.types';
import { ActionItem } from '../../services/actionService';
import ActivationMetricsWidget from '../../components/ActivationMetricsWidget';
import { activationService } from '../../services/activationService';
import { organizationService } from '../../services/organizationService';
import { getPillarDescription, PillarDescription } from '../../constants/pillarDescriptions';
import HelpCenter from '../../components/HelpCenter';
import OnboardingChecklist from '../../components/OnboardingChecklist';

const EarlyStageDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { selectedSite } = useSiteStore();
  const { modeData, isLoading: modeLoading, isEarlyStageMode } = useSiteMode(selectedSite?.id);
  const { readinessData, isLoading: readinessLoading } = useSiteReadiness(selectedSite?.id);
  const { actions, isLoading: actionsLoading, markAsInProgress, markAsDone, dismissAction, reopenAction, snoozeAction, isUpdating } = useActions(
    selectedSite?.id,
    {
      excludeLocalBusiness: true,
      statuses: ['PENDING', 'IN_PROGRESS'],
    }
  );
  const { stats } = useActionStats(selectedSite?.id);
  const { data: eligibility } = useTransitionEligibility(selectedSite?.id || 0);
  const { data: organization } = useQuery({
    queryKey: ['early-stage-organization', selectedSite?.organizationId],
    queryFn: () => organizationService.getOrganization(String(selectedSite?.organizationId)),
    enabled: Boolean(selectedSite?.organizationId),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const planTier = organization?.planTier;
  const [helpOpen, setHelpOpen] = useState(false);
  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Check scroll position
  const checkScroll = () => {
    if (scrollContainer) {
      setCanScrollLeft(scrollContainer.scrollLeft > 0);
      setCanScrollRight(
        scrollContainer.scrollLeft < scrollContainer.scrollWidth - scrollContainer.clientWidth - 1
      );
    }
  };

  useEffect(() => {
    checkScroll();
    // Scroll to start (leftmost position) on mount
    if (scrollContainer) {
      scrollContainer.scrollTo({ left: 0, behavior: 'auto' });
    }
  }, [scrollContainer, readinessData]);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainer) {
      const scrollAmount = 370; // Card width + gap
      const newScrollLeft = scrollContainer.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      scrollContainer.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
      setTimeout(checkScroll, 300);
    }
  };

  // Track dashboard view on mount
  useEffect(() => {
    if (selectedSite?.id && isEarlyStageMode) {
      const sessionId = sessionStorage.getItem('sessionId') || `session_${Date.now()}`;
      if (!sessionStorage.getItem('sessionId')) {
        sessionStorage.setItem('sessionId', sessionId);
      }
      
      activationService.trackEvent(selectedSite.id, {
        eventType: 'EARLY_STAGE_DASHBOARD_VIEWED',
        sessionId
      }).catch(err => console.error('Failed to track dashboard view:', err));
    }
  }, [selectedSite?.id, isEarlyStageMode]);

  if (!selectedSite) {
    return (
      <Box>
        <Alert severity="info">Please select a site to view readiness dashboard</Alert>
      </Box>
    );
  }

  if (modeLoading || readinessLoading) {
    return (
      <Box>
        <Typography variant="h4" component="h1" gutterBottom>
          Progress to Performance Mode
        </Typography>
        <LinearProgress sx={{ mt: 2 }} />
      </Box>
    );
  }

  // if (!isEarlyStageMode) {
  //   return (
  //     <Box sx={{ p: 3 }}>
  //       <Alert severity="success">
  //         Your site is in Performance Mode! You have access to the full analytics dashboard.
  //       </Alert>
  //     </Box>
  //   );
  // }

  return (
    <Box 
      sx={{ 
        bgcolor: '#f5f5f5',
        minHeight: '100vh',
        pb: 4
      }}
    >
      <Box 
        sx={{ 
          maxWidth: { xs: 400, sm: 768, md: 900, lg: 1200 }, // Fixed responsive widths
          mx: 'auto', // Center horizontally
          px: { xs: 2, sm: 3, md: 4 }, // Horizontal padding
          py: 3
        }}
      >
      {/* Mode Transition Status Widget */}
      {eligibility && (
        <Alert 
          severity={eligibility.eligible ? 'success' : 'info'}
          sx={{ 
            mb: 4,
            bgcolor: eligibility.eligible ? 'success.light' : '#E3F2FD',
            border: eligibility.eligible ? '1px solid' : '1px solid',
            borderColor: eligibility.eligible ? 'success.main' : '#2196F3',
            '& .MuiAlert-message': { width: '100%' },
            '& .MuiAlert-icon': { 
              color: eligibility.eligible ? 'success.main' : '#1976D2'
            }
          }}
        >
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={12} md={8}>
              <Typography variant="body1" fontWeight="600" gutterBottom sx={{ color: eligibility.eligible ? 'success.dark' : '#0D47A1' }}>
                {eligibility.eligible ? '🎉 Ready for Performance Mode!' : 'Progress to Performance Mode'}
              </Typography>
              <Typography variant="body2" sx={{ color: eligibility.eligible ? 'text.primary' : '#1565C0', lineHeight: 1.6 }}>
                {eligibility.message}
              </Typography>
              {eligibility.daysUntilEligible !== null && eligibility.daysUntilEligible > 0 && (
                <Typography variant="body2" sx={{ mt: 1, color: eligibility.eligible ? 'text.secondary' : '#1565C0', fontWeight: 500 }}>
                  {eligibility.gracePeriod 
                    ? `Auto-transition in ${eligibility.daysUntilEligible} days`
                    : `Estimated ${eligibility.daysUntilEligible} days until eligible`}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                variant="contained"
                onClick={() => navigate('/early-stage/timeline')}
                fullWidth
                sx={{ 
                  minHeight: { xs: 48, md: 40 },
                  fontSize: { xs: '1rem', md: '0.875rem' },
                  bgcolor: eligibility.eligible ? 'success.main' : 'primary.main',
                  '&:hover': { bgcolor: eligibility.eligible ? 'success.dark' : 'primary.dark' }
                }}
              >
                {eligibility.eligible ? 'Transition Now' : 'View Progress'}
              </Button>
            </Grid>
          </Grid>
        </Alert>
      )}
      
      {/* Onboarding Checklist - Show for new sites */}
      {selectedSite?.id && modeData?.siteAgeDays != null && modeData.siteAgeDays <= 14 && (
        <OnboardingChecklist siteId={selectedSite.id} planTier={planTier} />
      )}
      
      {/* Activation Metrics Widget */}
      {selectedSite?.id && (
        <Box sx={{ mt: 3 }}>
          <ActivationMetricsWidget siteId={selectedSite.id} planTier={planTier} />
        </Box>
      )}

      {/* Readiness Pillars */}
      <Box sx={{ mt: 4, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight={600}>
            Site SEO Readiness Pillars
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<TrendingUp />}
            onClick={() => navigate('/early-stage/readiness-history')}
            sx={{ 
              textTransform: 'none',
              fontSize: '0.875rem'
            }}
          >
            View Full History
          </Button>
        </Box>
      </Box>
      <Box sx={{ position: 'relative' }}>
        {/* Left Scroll Button */}
        {canScrollLeft && (
          <IconButton
            onClick={() => scroll('left')}
            sx={{
              position: 'absolute',
              left: -20,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              bgcolor: 'white',
              boxShadow: 3,
              '&:hover': {
                bgcolor: 'white',
                boxShadow: 6,
              },
              width: 48,
              height: 48,
            }}
          >
            <ChevronLeft fontSize="large" />
          </IconButton>
        )}

        {/* Right Scroll Button */}
        {canScrollRight && (
          <IconButton
            onClick={() => scroll('right')}
            sx={{
              position: 'absolute',
              right: -20,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              bgcolor: 'white',
              boxShadow: 3,
              '&:hover': {
                bgcolor: 'white',
                boxShadow: 6,
              },
              width: 48,
              height: 48,
            }}
          >
            <ChevronRight fontSize="large" />
          </IconButton>
        )}

        {/* Fade overlay on left */}
        {canScrollLeft && (
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 60,
              background: 'linear-gradient(to right, rgba(255,255,255,0.95), transparent)',
              zIndex: 1,
              pointerEvents: 'none',
            }}
          />
        )}

        {/* Fade overlay on right */}
        {canScrollRight && (
          <Box
            sx={{
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 0,
              width: 60,
              background: 'linear-gradient(to left, rgba(255,255,255,0.95), transparent)',
              zIndex: 1,
              pointerEvents: 'none',
            }}
          />
        )}

        <Box 
          ref={setScrollContainer}
          onScroll={checkScroll}
          sx={{ 
            display: 'flex',
            overflowX: 'auto',
            gap: 3,
            pb: 2,
            pl: { xs: 2, sm: 3, md: 4 },
            pr: { xs: 2, sm: 3, md: 4 },
            scrollSnapType: 'x mandatory',
            scrollBehavior: 'smooth',
            '&::-webkit-scrollbar': {
              height: 8,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#f1f1f1',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#888',
              borderRadius: 4,
              '&:hover': {
                backgroundColor: '#555',
              },
            },
          }}
        >
          {readinessData?.pillars
            .slice() // Create a copy to avoid mutating original array
            .sort((a, b) => {
              // Define desired display order
              const order = ['technical_hygiene', 'indexability', 'internal_linking', 'performance', 'content_intent', 'ai_searchability'];
              return order.indexOf(a.pillarId) - order.indexOf(b.pillarId);
            })
            .map((pillar) => (
              <Box 
                key={pillar.pillarId}
                sx={{ 
                  width: { xs: 200, sm: 230, md: 260 },
                  flexShrink: 0,
                  scrollSnapAlign: 'start',
                }}
              >
                <ReadinessPillarCard pillar={pillar} />
              </Box>
            ))}
        </Box>
      </Box>

      {/* Action Queue */}
      <Box sx={{ mt: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" fontWeight={600}>
            🎯 Action Queue
          </Typography>
          <Box display="flex" gap={2} alignItems="center">
            {stats && (
              <Chip
                label={`${stats.totalCompleted} completed • ${stats.completionRate}% done`}
                color="success"
                variant="outlined"
              />
            )}
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate('/actions-next')}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              View All {actions.length} Actions →
            </Button>
          </Box>
        </Box>
        
        {actionsLoading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (() => {
          const displayActions = actions;
          return displayActions.length > 0 ? (
            <>
              <Grid container spacing={2}>
                {displayActions.slice(0, 5).map((action) => (
                  <Grid item xs={12} key={action.id}>
                    <EnhancedActionCard 
                      action={action} 
                      index={actions.indexOf(action)}
                      onStart={markAsInProgress}
                      onMarkDone={markAsDone}
                      onDismiss={dismissAction}
                      onReopen={reopenAction}
                      onSnooze={snoozeAction}
                      isUpdating={isUpdating}
                    />
                  </Grid>
                ))}
              </Grid>
              
              {displayActions.length > 5 && (
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/actions-next')}
                    sx={{ textTransform: 'none', minWidth: 200, fontWeight: 600 }}
                  >
                    View All {displayActions.length} Actions
                  </Button>
                </Box>
              )}
            </>
          ) : (
            <Card sx={{ bgcolor: 'primary.light', border: '2px solid', borderColor: 'primary.main' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                  <Box flex={1}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      ✅ No Critical Actions
                    </Typography>
                    <Typography variant="body1">
                      {actions.length > 0 ? (
                        <>Great progress! You have {actions.length} other actions to continue improving your SEO.</>
                      ) : (
                        <>Excellent work! You've completed all recommended actions.</>
                      )}
                    </Typography>
                  </Box>
                  {actions.length > 0 && (
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => navigate('/actions-next')}
                      sx={{ 
                        minWidth: 200,
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 600
                      }}
                    >
                      View All Actions →
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          );
        })()}
      </Box>

      {/* Floating Help Button */}
      <Tooltip title="Help & Support">
        <IconButton
          onClick={() => setHelpOpen(true)}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            bgcolor: 'primary.main',
            color: 'white',
            width: 56,
            height: 56,
            boxShadow: 3,
            '&:hover': {
              bgcolor: 'primary.dark',
              boxShadow: 6
            },
            zIndex: 1000
          }}
        >
          <HelpOutline fontSize="large" />
        </IconButton>
      </Tooltip>

      {/* Help Center Drawer */}
      <HelpCenter open={helpOpen} onClose={() => setHelpOpen(false)} />
      </Box>
    </Box>
  );
};

// Status Banner Component
const StatusBanner: React.FC<{ modeData: any; readinessData: any }> = ({ modeData, readinessData }) => {
  const progress = modeData?.readinessProgress || 0;

  return (
    <Card sx={{ bgcolor: '#f5f5f5', border: '2px solid #e0e0e0' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Early Stage Mode (Day {modeData?.siteAgeDays || 0})
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {modeData?.gscDataDays || 0} days of search data collected
            </Typography>
          </Box>
          <Chip
            label={`${progress}% Ready`}
            color={progress >= 80 ? 'success' : progress >= 50 ? 'warning' : 'default'}
            sx={{ fontSize: '1rem', fontWeight: 600 }}
          />
        </Box>

        <Box>
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography variant="body2" fontWeight={500}>
              SEO Maturity Progress
            </Typography>
            <Typography variant="body2" color="primary">
              {readinessData?.daysUntilReady || 30} days until performance-ready
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 12,
              borderRadius: 6,
              bgcolor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                borderRadius: 6,
                bgcolor: progress >= 80 ? '#4caf50' : progress >= 50 ? '#ff9800' : '#2196f3'
              }
            }}
          />
        </Box>

        {/* Reason codes */}
        {modeData?.reasonCodes && modeData.reasonCodes.length > 0 && (
          <Box mt={2}>
            <Typography variant="caption" color="text.secondary">
              Why early-stage: {modeData.reasonCodes.map((code: string) => 
                code.replace(/_/g, ' ').toLowerCase()
              ).join(', ')}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// Readiness Pillar Card Component
const ReadinessPillarCard: React.FC<{ pillar: ReadinessPillar }> = ({ pillar }) => {
  const navigate = useNavigate();
  const { selectedSite } = useSiteStore();
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const pillarDescription = getPillarDescription(pillar.pillarId);
  
  const handlePillarClick = () => {
    // Track pillar view event
    if (selectedSite?.id) {
      const sessionId = sessionStorage.getItem('sessionId') || `session_${Date.now()}`;
      activationService.trackEvent(selectedSite.id, {
        eventType: 'READINESS_PILLAR_VIEWED',
        sessionId,
        metadata: { 
          pillarId: pillar.pillarId,
          pillarName: pillar.pillarName,
          score: pillar.score
        }
      }).catch(err => console.error('Failed to track pillar view:', err));
    }
    
    // Special routing for specific pillars to existing pages
    if (pillar.pillarId === 'indexability') {
      navigate('/customer/url-coverage');
    } else if (pillar.pillarId === 'technical_hygiene') {
      navigate('/early-stage/readiness/technical_hygiene');
    } else if (pillar.pillarId === 'internal_linking') {
      navigate('/customer/internal-links');
    } else if (pillar.pillarId === 'performance') {
      navigate('/customer/core-web-vitals');
    } else if (pillar.pillarId === 'content_intent') {
      navigate('/customer/content-analysis');
    } else if (pillar.pillarId === 'ai_searchability') {
      navigate('/early-stage/readiness/ai_searchability');
    } else {
      navigate(`/early-stage/readiness/${pillar.pillarId}`);
    }
  };
  
  const handleInfoClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setInfoDialogOpen(true);
  };

  // Extract key metrics from summary text based on pillar type
  const getKeyMetrics = () => {
    const summary = pillar.summary || '';
    
    switch (pillar.pillarId) {
      case 'internal_linking':
        if (typeof pillar.totalPages === 'number' && typeof pillar.pagesWithLinks === 'number') {
          return {
            label: 'Pages with Links',
            value: `${Math.max(0, Math.min(pillar.pagesWithLinks, pillar.totalPages))} of ${pillar.totalPages}`,
            secondary: typeof pillar.orphanPages === 'number' && pillar.orphanPages > 0 ? `${pillar.orphanPages} orphan pages` : null
          };
        }
        const orphanMatch = summary.match(/(\d+)\s+pages analyzed,\s+(\d+)\s+with incoming links/);
        const orphanIssue = pillar.issues.find(i => i.includes('orphan'));
        const orphanCount = orphanIssue?.match(/(\d+)\s+orphan/)?.[1];
        return orphanMatch ? {
          label: 'Pages with Links',
          value: `${orphanMatch[2]} of ${orphanMatch[1]}`,
          secondary: orphanCount ? `${orphanCount} orphan pages` : null
        } : null;
      
      case 'indexability':
        const indexMatch = summary.match(/(\d+)\s+pages indexed,\s+(\d+)\s+appearing/);
        return indexMatch ? {
          label: 'Indexed',
          value: `${indexMatch[1]} pages`,
          secondary: `${indexMatch[2]} in search`
        } : null;
      
      case 'performance':
        const mobileMatch = summary.match(/Mobile:\s+(\d+)\/100/);
        const desktopMatch = summary.match(/Desktop:\s+(\d+)\/100/);
        return {
          label: 'Performance',
          value: mobileMatch ? `Mobile: ${mobileMatch[1]}/100` : null,
          secondary: desktopMatch ? `Desktop: ${desktopMatch[1]}/100` : null
        };
      
      case 'content_intent':
        const contentMatch = summary.match(/(\d+)\s+pages available,\s+(\d+)\s+with adequate/);
        return contentMatch ? {
          label: 'Content Quality',
          value: `${contentMatch[2]} of ${contentMatch[1]}`,
          secondary: 'pages with adequate content'
        } : null;
      
      case 'technical_hygiene':
        const techMatch = summary.match(/(\d+)\s+pages analyzed/);
        return techMatch ? {
          label: 'Pages Analyzed',
          value: techMatch[1],
          secondary: pillar.issues.length === 0 ? 'No critical issues' : null
        } : null;
      
      case 'ai_searchability': {
        const aiMatch = summary.match(/(\d+)\/15 raw/);
        const gptMatch = summary.match(/GPTBot: (\w+)/);
        return {
          label: 'AI Score',
          value: aiMatch ? `${aiMatch[1]}/15` : null,
          secondary: gptMatch ? `GPTBot: ${gptMatch[1]}` : null
        };
      }
      
      default:
        return null;
    }
  };

  const keyMetrics = getKeyMetrics();

  // Pillar theme colors
  const getPillarColor = () => {
    switch (pillar.pillarId) {
      case 'technical_hygiene': return { main: '#14B8A6', light: '#D1FAE5', dark: '#0D9488' };
      case 'indexability': return { main: '#F59E0B', light: '#FEF3C7', dark: '#D97706' };
      case 'internal_linking': return { main: '#3B82F6', light: '#DBEAFE', dark: '#2563EB' };
      case 'performance': return { main: '#06B6D4', light: '#CFFAFE', dark: '#0891B2' };
      case 'content_intent': return { main: '#8B5CF6', light: '#EDE9FE', dark: '#7C3AED' };
      case 'ai_searchability': return { main: '#EC4899', light: '#FCE7F3', dark: '#BE185D' };
      default: return { main: '#64748B', light: '#F1F5F9', dark: '#475569' };
    }
  };

  const colors = getPillarColor();

  const getStatusText = () => {
    if (pillar.score >= 90) return 'Excellent';
    if (pillar.score >= 75) return 'Good';
    if (pillar.score >= 50) return 'Fair';
    return 'Needs Improvement';
  };
  
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'READY':
        return <CheckCircle sx={{ color: '#4caf50', fontSize: 32 }} />;
      case 'PARTIAL':
        return <Warning sx={{ color: '#ff9800', fontSize: 32 }} />;
      case 'NOT_READY':
        return <ErrorIcon sx={{ color: '#f44336', fontSize: 32 }} />;
      default:
        return <HourglassEmpty sx={{ color: '#9e9e9e', fontSize: 32 }} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'READY': return '#e8f5e9';
      case 'PARTIAL': return '#fff3e0';
      case 'NOT_READY': return '#ffebee';
      default: return '#f5f5f5';
    }
  };

  // Count critical and warning issues
  const criticalCount = pillar.issues?.filter(issue => 
    issue.toLowerCase().includes('critical') || 
    issue.toLowerCase().includes('error')
  ).length || 0;
  
  const warningCount = pillar.issues?.filter(issue => 
    !issue.toLowerCase().includes('critical') && 
    !issue.toLowerCase().includes('error')
  ).length || 0;

  return (
    <>
      <Card 
        sx={{ 
          width: '100%',
          height: 580,
          bgcolor: '#ffffff',
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '1px solid #e0e0e0',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            transform: 'translateY(-2px)',
          }
        }}
      >
        <CardContent sx={{ p: 2.5, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Typography 
              variant="h6" 
              fontWeight={600} 
              sx={{ 
                color: 'text.primary', 
                fontSize: '0.95rem',
                lineHeight: 1.3,
                flex: 1,
                mr: 1,
                wordBreak: 'break-word',
                overflowWrap: 'break-word'
              }}
            >
              {pillar.pillarName}
            </Typography>
            <Tooltip title="Learn more">
              <IconButton size="small" onClick={handleInfoClick} sx={{ mt: -0.5, flexShrink: 0 }}>
                <InfoOutlined fontSize="small" sx={{ color: 'text.secondary' }} />
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
            <Box sx={{ position: 'relative', width: 85, height: 85 }}>
              <svg width="85" height="85" viewBox="0 0 85 85" style={{ transform: 'rotate(-90deg)' }}>
                <circle cx="42.5" cy="42.5" r="37" fill="none" stroke="#E5E7EB" strokeWidth="6" />
                <circle cx="42.5" cy="42.5" r="37" fill="none" stroke={colors.main} strokeWidth="6" strokeDasharray={`${(pillar.score / 100) * 232.5} 232.5`} strokeLinecap="round" />
              </svg>
              <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center'
              }}>
                <Typography variant="h3" fontWeight={700} sx={{ lineHeight: 1, color: colors.main, fontSize: '2.2rem' }}>
                  {pillar.score}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 500 }}>
                  /100
                </Typography>
              </Box>
            </Box>

            {/* Status Badge - Centered below circle */}
            <Box sx={{ mt: 1.5 }}>
              <Chip
                label={getStatusText()}
                size="small"
                sx={{ 
                  bgcolor: colors.light,
                  color: colors.main,
                  fontWeight: 600,
                  fontSize: '0.72rem',
                  px: 1.5,
                  height: 22
                }}
              />
            </Box>
          </Box>

          {/* Warning Badge - Centered */}
          {(criticalCount > 0 || warningCount > 0) && (
            <Box display="flex" gap={1} justifyContent="center" mb={1.5}>
              {warningCount > 0 && (
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Warning sx={{ fontSize: 16, color: '#F59E0B' }} />
                  <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#F59E0B', fontWeight: 600 }}>
                    Warning {warningCount}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Issues Section or No Issues Message */}
          <Box mb={2} sx={{ minHeight: 160 }}>
            {pillar.issues && pillar.issues.length > 0 ? (
              <Box>
                <Typography variant="caption" sx={{ 
                  color: 'text.secondary', 
                  fontSize: '0.7rem', 
                  display: 'block', 
                  mb: 1, 
                  textTransform: 'uppercase', 
                  fontWeight: 700,
                  letterSpacing: '0.5px'
                }}>
                  ISSUES
                </Typography>
                <Box sx={{ 
                  maxHeight: 130, 
                  overflowY: 'auto', 
                  pr: 0.5,
                  '&::-webkit-scrollbar': { width: '4px' },
                  '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1', borderRadius: '4px' },
                  '&::-webkit-scrollbar-thumb': { backgroundColor: '#d0d0d0', borderRadius: '4px', '&:hover': { backgroundColor: '#b0b0b0' } }
                }}>
                  {pillar.issues.slice(0, 3).map((issue, idx) => (
                    <Typography 
                      key={idx} 
                      variant="body2" 
                      sx={{ 
                        fontSize: '0.78rem', 
                        color: 'text.primary', 
                        lineHeight: 1.5,
                        mb: 1,
                        pl: 1.5,
                        position: 'relative',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        '&:before': {
                          content: '"•"',
                          position: 'absolute',
                          left: 0,
                          color: '#DC2626',
                          fontWeight: 700
                        }
                      }}
                    >
                      {issue}
                    </Typography>
                  ))}
                  {pillar.issues.length > 3 && (
                    <Typography variant="caption" sx={{ fontSize: '0.72rem', color: 'text.secondary', fontStyle: 'italic', pl: 1.5, display: 'block', mt: 0.5 }}>
                      +{pillar.issues.length - 3} more issues
                    </Typography>
                  )}
                </Box>
              </Box>
            ) : (
              <Box textAlign="center" display="flex" alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
                <Typography variant="body2" sx={{ fontSize: '0.85rem', color: '#10B981', fontWeight: 500 }}>
                  ✓ No issues found
                </Typography>
              </Box>
            )}
          </Box>

          {/* Metrics Section */}
          <Box mb={2} sx={{ minHeight: 90 }}>
            {keyMetrics && (
              <Box pb={2} borderTop="1px solid #e0e0e0" pt={2}>
                <Typography variant="caption" sx={{ 
                  color: 'text.secondary', 
                  fontSize: '0.65rem', 
                  display: 'block', 
                  mb: 0.75, 
                  textTransform: 'uppercase', 
                  fontWeight: 700,
                  letterSpacing: '0.5px'
                }}>
                  {keyMetrics.label}
                </Typography>
                <Typography variant="h6" sx={{ fontSize: '0.95rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.3 }}>
                  {keyMetrics.value}
                </Typography>
                {keyMetrics.secondary && (
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block', mt: 0.5 }}>
                    {keyMetrics.secondary}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {/* View Details Button */}
          <Button
            fullWidth
            variant="contained"
            onClick={handlePillarClick}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: colors.main,
              color: 'white',
              py: 1.25,
              fontSize: '0.85rem',
              borderRadius: 1.5,
              boxShadow: 'none',
              mt: 'auto',
              '&:hover': {
                bgcolor: colors.dark,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
              }
            }}
          >
            View Details
          </Button>
        </CardContent>
      </Card>

      {/* Info Dialog */}
      {pillarDescription && (
        <Dialog 
          open={infoDialogOpen} 
          onClose={() => setInfoDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              {getStatusIcon(pillar.status)}
              <Typography variant="h6" fontWeight={600}>
                {pillarDescription.name}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Box mb={3}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Overview
              </Typography>
              <Typography variant="body2" paragraph>
                {pillarDescription.shortDescription}
              </Typography>
            </Box>

            <Box mb={3}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Scope
              </Typography>
              <Typography variant="body2" paragraph>
                {pillarDescription.scope}
              </Typography>
            </Box>

            <Box mb={3}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Why It Matters
              </Typography>
              <Typography variant="body2" paragraph>
                {pillarDescription.whyItMatters}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                What We Measure
              </Typography>
              <List dense sx={{ pt: 0 }}>
                {pillarDescription.whatWeMeasure.map((item, idx) => (
                  <ListItem key={idx} sx={{ py: 0.5, px: 0 }}>
                    <CheckCircle sx={{ fontSize: 16, color: 'success.main', mr: 1 }} />
                    <ListItemText 
                      primary={item}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>

            <Box mt={3} p={2} bgcolor="info.light" borderRadius={1}>
              <Typography variant="body2" fontWeight={600} gutterBottom>
                Current Status
              </Typography>
              <Typography variant="body2">
                Score: {pillar.score}/100 • {pillar.status === 'READY' ? '✅ Ready' : pillar.status === 'PARTIAL' ? '⚠️ Partial' : '❌ Needs Work'}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setInfoDialogOpen(false)} color="primary">
              Close
            </Button>
            <Button 
              onClick={() => {
                setInfoDialogOpen(false);
                handlePillarClick();
              }} 
              variant="contained" 
              color="primary"
            >
              View Details
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

const MetricPill: React.FC<{
  label: string;
  value: string;
  accent?: string;
  tone?: 'success' | 'warning' | 'default';
  icon?: React.ReactNode;
}> = ({ label, value, accent, tone = 'default', icon }) => {
  const toneColor = tone === 'success' ? '#15803D' : tone === 'warning' ? '#B45309' : '#475569';
  const valueColor = accent || toneColor;

  return (
    <Box
      sx={{
        border: '1px solid #E2E8F0',
        borderRadius: 1.5,
        bgcolor: '#FFFFFF',
        px: 1.25,
        py: 1,
        minWidth: 0,
      }}
    >
      <Typography variant="caption" sx={{ display: 'block', color: '#64748B', fontWeight: 700, lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Box display="flex" alignItems="center" gap={0.5} minWidth={0}>
        {icon}
        <Typography
          variant="body2"
          sx={{
            color: valueColor,
            fontWeight: 700,
            lineHeight: 1.3,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
};

// Enhanced Action Card Component with Mark as Done functionality
const EnhancedActionCard: React.FC<{ 
  action: ActionItem; 
  index: number;
  onStart: (id: number) => void;
  onMarkDone: (id: number) => void;
  onDismiss: (id: number, reason?: string) => void;
  onReopen: (id: number, reason?: string) => void;
  onSnooze: (id: number, days: number) => void;
  isUpdating: boolean;
}> = ({ action, index, onStart, onMarkDone, onDismiss, onReopen, onSnooze, isUpdating }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [dismissDialogOpen, setDismissDialogOpen] = useState(false);
  const [dismissReason, setDismissReason] = useState('');
  const [snoozeDialogOpen, setSnoozeDialogOpen] = useState(false);
  const { selectedSite } = useSiteStore();
  const remarksNote = action.remarks || action.notes;
  const normalizedStatus = String(action.status || '').toUpperCase();
  const normalizedSeverity = normalizeLevel(action.severity);
  const impactLevel = normalizeLevel(action.expectedImpact || action.context?.impactLevel);
  const effortLevel = normalizeLevel(action.effortLevel || action.context?.effortLevel);
  const estimatedMinutes = action.estimatedMinutes || action.context?.estimatedMinutes;
  const supportingText = action.description || action.rationale;
  const valueStatement = action.whyItMatters || action.context?.whyItMatters;

  const handleMarkDone = (actionId: number) => {
    // Track action completion event
    if (selectedSite?.id) {
      const sessionId = sessionStorage.getItem('sessionId') || `session_${Date.now()}`;
      activationService.trackEvent(selectedSite.id, {
        eventType: 'ACTION_COMPLETED',
        sessionId,
        metadata: {
          actionId: actionId,
          actionTitle: action.title,
          category: action.category,
          severity: action.severity
        }
      }).catch(err => console.error('Failed to track action completion:', err));
    }
    onMarkDone(actionId);
  };

  // Helper to normalize impact/effort values to uppercase for display
  function normalizeLevel(level?: string): string {
    if (!level) return '';
    return level.toUpperCase();
  }

  const getImpactColor = (impact?: string) => {
    const normalized = normalizeLevel(impact);
    switch (normalized) {
      case 'CRITICAL': return '#f44336';
      case 'HIGH': return '#ff9800';
      case 'MEDIUM': return '#2196f3';
      case 'LOW': return '#4caf50';
      default: return '#9e9e9e';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'CRITICAL': return '#f44336';
      case 'HIGH': return '#ff9800';
      case 'MEDIUM': return '#2196f3';
      case 'LOW': return '#4caf50';
      default: return '#9e9e9e';
    }
  };

  const getEffortColor = (effort?: string): 'success' | 'warning' | 'default' => {
    const normalized = normalizeLevel(effort);
    switch (normalized) {
      case 'LOW': return 'success';
      case 'MEDIUM': return 'warning';
      case 'HIGH': return 'default';
      default: return 'default';
    }
  };

  const getCardVisual = () => {
    if (normalizedStatus === 'IN_PROGRESS') {
      return {
        label: 'In progress',
        accent: '#2563EB',
        background: '#EFF6FF',
        border: '#BFDBFE',
        icon: <RocketLaunch sx={{ fontSize: 18 }} />,
        cta: 'Mark Complete',
      };
    }

    if (['CRITICAL', 'HIGH'].includes(normalizedSeverity) || ['CRITICAL', 'HIGH'].includes(impactLevel)) {
      return {
        label: 'Priority action',
        accent: '#DC2626',
        background: '#FEF2F2',
        border: '#FECACA',
        icon: <ErrorIcon sx={{ fontSize: 18 }} />,
        cta: normalizedStatus === 'PENDING' ? 'Start' : 'Mark Complete',
      };
    }

    return {
      label: 'Recommended',
      accent: '#0F766E',
      background: '#F0FDFA',
      border: '#99F6E4',
      icon: <CheckCircle sx={{ fontSize: 18 }} />,
      cta: normalizedStatus === 'PENDING' ? 'Start' : 'Mark Complete',
    };
  };

  const cardVisual = getCardVisual();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDismiss = () => {
    onDismiss(action.id, dismissReason.trim() || undefined);
    setDismissDialogOpen(false);
    setDismissReason('');
    handleMenuClose();
  };

  const handleOpenDismissDialog = () => {
    setDismissDialogOpen(true);
    handleMenuClose();
  };

  const handleSnooze = (days: number) => {
    onSnooze(action.id, days);
    setSnoozeDialogOpen(false);
    handleMenuClose();
  };

  const handleOpenSnoozeDialog = () => {
    setSnoozeDialogOpen(true);
    handleMenuClose();
  };

  const handleReopen = () => {
    onReopen(action.id, 'Reopened from early-stage dashboard');
    handleMenuClose();
  };

  const handleCloseDismissDialog = () => {
    setDismissDialogOpen(false);
    setDismissReason('');
  };

  const canReopen = ['DONE', 'COMPLETED', 'CANCELLED', 'DISMISSED', 'AUTO_RESOLVED', 'ARCHIVED'].includes(
    normalizedStatus
  );

  return (
    <Card 
      sx={{ 
        border: '1px solid #E5E7EB',
        borderLeft: `4px solid ${cardVisual.accent}`,
        borderRadius: 2,
        position: 'relative',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
        transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
        '&:hover': {
          borderColor: '#CBD5E1',
          boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
        }
      }}
    >
      <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
        <Box display="flex" alignItems="stretch" gap={2} sx={{ flexDirection: { xs: 'column', md: 'row' } }}>
          <Box
            sx={{
              width: { xs: '100%', md: 96 },
              flexShrink: 0,
              borderRadius: 1.5,
              bgcolor: cardVisual.background,
              border: `1px solid ${cardVisual.border}`,
              color: cardVisual.accent,
              display: 'flex',
              flexDirection: { xs: 'row', md: 'column' },
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              py: { xs: 1, md: 1.5 },
              px: 1.25,
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 'inset 0 0 0 1px rgba(15, 23, 42, 0.08)',
                flexShrink: 0,
              }}
            >
              {cardVisual.icon}
            </Box>
            <Box textAlign={{ xs: 'left', md: 'center' }}>
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, lineHeight: 1.2 }}>
                #{index + 1}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, lineHeight: 1.2 }}>
                {cardVisual.label}
              </Typography>
            </Box>
          </Box>

          <Box flex={1} minWidth={0}>
            <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2} mb={1}>
              <Box minWidth={0}>
                <Typography 
                  variant="h6" 
                  fontWeight={700}
                  sx={{
                    fontSize: '1rem',
                    lineHeight: 1.35,
                    color: '#0F172A',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                  }}
                >
                  {action.title}
                </Typography>
                {action.category && (
                  <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {action.category}
                  </Typography>
                )}
              </Box>
              <Chip
                label={String(action.status || '').replace('_', ' ')}
                size="small"
                sx={{
                  bgcolor: normalizedStatus === 'IN_PROGRESS' ? '#DBEAFE' : '#F8FAFC',
                  color: normalizedStatus === 'IN_PROGRESS' ? '#1D4ED8' : '#475569',
                  border: '1px solid',
                  borderColor: normalizedStatus === 'IN_PROGRESS' ? '#BFDBFE' : '#E2E8F0',
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              />
            </Box>

            <Typography 
              variant="body2" 
              sx={{ 
                color: '#475569',
                mb: 2,
                lineHeight: 1.6,
                maxWidth: 760,
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
              }}
            >
              {supportingText}
            </Typography>

            {valueStatement && (
              <Box
                sx={{
                  bgcolor: '#F8FAFC',
                  px: 1.5,
                  py: 1.25,
                  borderRadius: 1.5,
                  border: '1px solid #E2E8F0',
                  mb: 2
                }}
              >
                <Typography variant="caption" sx={{ display: 'block', color: '#64748B', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                  Business impact
                </Typography>
                <Typography variant="body2" sx={{ color: '#334155', lineHeight: 1.55 }}>
                  {valueStatement}
                </Typography>
              </Box>
            )}

            {action.status === 'PENDING' && remarksNote && (
              <Box
                sx={{
                  bgcolor: '#EFF6FF',
                  px: 1.5,
                  py: 1.25,
                  borderRadius: 1.5,
                  border: '1px solid #BFDBFE',
                  mb: 2,
                }}
              >
                <Typography variant="caption" sx={{ display: 'block', color: '#1D4ED8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>
                  Re-opened Remark
                </Typography>
                <Typography variant="body2" sx={{ color: '#1E3A8A', lineHeight: 1.55 }}>{remarksNote}</Typography>
              </Box>
            )}

            <Box display="grid" gap={1} sx={{ gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, minmax(0, 1fr))' } }}>
              {effortLevel && (
                <MetricPill label="Effort" value={effortLevel} tone={getEffortColor(effortLevel)} />
              )}
              {estimatedMinutes && (
                <MetricPill label="Time" value={`${estimatedMinutes} min`} icon={<Schedule sx={{ fontSize: 15 }} />} />
              )}
              {impactLevel && (
                <MetricPill label="Impact" value={impactLevel} accent={getImpactColor(impactLevel)} />
              )}
              <MetricPill label="Severity" value={normalizedSeverity || 'NORMAL'} accent={getSeverityColor(action.severity)} />
            </Box>
          </Box>

          <Box 
            display="flex" 
            gap={1} 
            alignItems="stretch"
            sx={{
              flexDirection: { xs: 'row', md: 'column' },
              justifyContent: { xs: 'flex-end', md: 'space-between' },
              minWidth: { md: 156 },
              borderLeft: { md: '1px solid #E5E7EB' },
              pl: { md: 2 },
            }}
          >
            {action.status === 'PENDING' ? (
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<PlayArrow />}
                onClick={() => onStart(action.id)}
                disabled={isUpdating}
                sx={{ minHeight: 40, minWidth: { xs: 112, md: '100%' }, textTransform: 'none', fontWeight: 700, boxShadow: 'none' }}
              >
                {cardVisual.cta}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="success"
                size="small"
                startIcon={<Check />}
                onClick={() => handleMarkDone(action.id)}
                disabled={isUpdating}
                sx={{ minHeight: 40, minWidth: { xs: 150, md: '100%' }, textTransform: 'none', fontWeight: 700, boxShadow: 'none' }}
              >
                {cardVisual.cta}
              </Button>
            )}
            <IconButton
              size="medium"
              onClick={handleMenuOpen}
              disabled={isUpdating}
              sx={{ minHeight: 40, minWidth: 40, border: '1px solid #E2E8F0', borderRadius: 1 }}
            >
              <MoreVert />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              {canReopen ? (
                <MenuItem onClick={handleReopen}>
                  <Replay sx={{ mr: 1 }} fontSize="small" />
                  Reopen Action
                </MenuItem>
              ) : (
                [
                  <MenuItem key="snooze" onClick={handleOpenSnoozeDialog}>
                    <HourglassEmpty sx={{ mr: 1 }} fontSize="small" />
                    Snooze
                  </MenuItem>,
                  <MenuItem key="dismiss" onClick={handleOpenDismissDialog}>
                    <Close sx={{ mr: 1 }} fontSize="small" />
                    Dismiss
                  </MenuItem>,
                ]
              )}
            </Menu>
          </Box>
        </Box>
      </CardContent>

      <Dialog open={dismissDialogOpen} onClose={handleCloseDismissDialog} fullWidth maxWidth="sm">
        <DialogTitle>Dismiss Action</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add a note for why you are dismissing this action.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            maxRows={6}
            label="Dismissal reason (optional)"
            value={dismissReason}
            onChange={(e) => setDismissReason(e.target.value)}
            placeholder="Example: Not relevant for this site right now"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDismissDialog}>Cancel</Button>
          <Button onClick={handleDismiss} color="warning" variant="contained" disabled={isUpdating}>
            Dismiss Action
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={snoozeDialogOpen} onClose={() => setSnoozeDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Snooze Action</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Remind me again in…
          </Typography>
          <Box display="flex" flexDirection="column" gap={1}>
            {[1, 3, 7, 14].map((days) => (
              <Button
                key={days}
                variant="outlined"
                onClick={() => handleSnooze(days)}
                disabled={isUpdating}
                fullWidth
              >
                {days === 1 ? 'Tomorrow (1 day)' : `${days} days`}
              </Button>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSnoozeDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default EarlyStageDashboard;
